/**
 * Desktop Sidebar Navigation
 *
 * Features:
 * - Fixed position navigation
 * - Visual indicator for active route
 * - User profile section with welcome message
 * - Logo placeholder
 * - Sign out action
 */

import { NavLink } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import './Sidebar.css';

interface NavItem {
  to: string;
  label: string;
  icon: string;
}

const navItems: NavItem[] = [
  { to: '/gym', label: 'Gym Tracker', icon: '💪' },
  { to: '/schedule', label: 'Schedule', icon: '📅' },
  { to: '/notes', label: 'Notes', icon: '📝' },
  { to: '/analytics', label: 'Analytics', icon: '📊' },
];

function getDisplayName(email: string | undefined): string {
  if (!email) return 'Guest';
  const name = email.split('@')[0];
  if (!name) return 'User';
  return name.charAt(0).toUpperCase() + name.slice(1);
}

export function Sidebar(): JSX.Element {
  const { user, signOut } = useAuth();
  const displayName = getDisplayName(user?.email);

  return (
    <aside className="sidebar">
      <div className="sidebar__header">
        <div className="sidebar__logo-container">
          <div className="sidebar__logo-icon">
            <span className="sidebar__logo-placeholder">M</span>
          </div>
          <h1 className="sidebar__logo">My OS</h1>
        </div>
      </div>

      <div className="sidebar__welcome">
        <span className="sidebar__welcome-text">Welcome, {displayName}</span>
      </div>

      <nav className="sidebar__nav">
        {navItems.map((item) => (
          <NavLink
            key={item.to}
            to={item.to}
            className={({ isActive }) =>
              `sidebar__nav-item ${isActive ? 'sidebar__nav-item--active' : ''}`
            }
          >
            <span className="sidebar__nav-icon">{item.icon}</span>
            <span className="sidebar__nav-label">{item.label}</span>
          </NavLink>
        ))}
      </nav>

      <div className="sidebar__footer">
        <div className="sidebar__user">
          <div className="sidebar__user-avatar">
            {user?.email?.charAt(0).toUpperCase() ?? 'U'}
          </div>
          <div className="sidebar__user-info">
            <div className="sidebar__user-email">{user?.email ?? 'Guest'}</div>
          </div>
        </div>
        <button
          className="sidebar__signout"
          onClick={() => signOut()}
          type="button"
        >
          Sign Out
        </button>
      </div>
    </aside>
  );
}
